import React, { FC, useState } from 'react';
import { navigate } from 'gatsby';
import { CustomSuitabilityCheckerProps } from './models';
import { HeadingBlock } from '@design-system/heading-block';
import { Button } from '@design-system/button';
import { CheckboxList } from '@design-system/checkbox-list';
import { CheckboxField } from '@design-system/checkbox-field';
import { Container } from '@design-system/container';
import { Image } from '@design-system/image';
import { Modal } from '@design-system/modal';
import { Typography } from '@design-system/typography';
import {
  areAllQuestionsAnswered,
  downloadSuitabilitySummaryImage,
  getSubmitResultMessage,
  SelectedAnswer,
  updateMultipleChoiceAnswers,
  updateSingleChoiceAnswers,
  validateSuitabilityCheckerModelData,
} from './helpers';

import './SuitabilityChecker.scss';

export const SuitabilityChecker: FC<CustomSuitabilityCheckerProps> = ({ modelData }) => {
  const {
    heading,
    questions,
    submit_button_label,
    submit_error_message,
    positive_result_message,
    negative_result_message,
    download_button_label,
    disclaimer,
    popup_image,
  } = validateSuitabilityCheckerModelData(modelData);

  const [selectedAnswersByQuestion, setSelectedAnswersByQuestion] = useState<
    Record<string, SelectedAnswer[]>
  >({});
  const [submitResultMessage, setSubmitResultMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  const handleSingleChoiceAnswerClick = (
    questionKey: string,
    answer: string,
    isSuitable: boolean
  ) => {
    setSelectedAnswersByQuestion((prev) =>
      updateSingleChoiceAnswers(prev, questionKey, answer, isSuitable)
    );
  };

  const handleMultipleChoiceAnswerChange = (
    questionKey: string,
    answer: string,
    isSuitable: boolean,
    checked: boolean
  ) => {
    setSelectedAnswersByQuestion((prev) =>
      updateMultipleChoiceAnswers(prev, questionKey, answer, isSuitable, checked)
    );
  };

  const handleSubmit = () => {
    if (!areAllQuestionsAnswered(questions, selectedAnswersByQuestion)) {
      setShowValidationError(true);
      return;
    }

    setShowValidationError(false);
    setSubmitResultMessage(
      getSubmitResultMessage(
        selectedAnswersByQuestion,
        positive_result_message,
        negative_result_message
      )
    );
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDownloadImage = () => {
    downloadSuitabilitySummaryImage({
      heading,
      questions,
      selectedAnswersByQuestion,
      submitResultMessage,
      disclaimer,
      popupImage: popup_image ?? null,
    });
  };

  return (
    <Container maxWidth="max-grid">
      <HeadingBlock
        className="cds-page-header__heading"
        align="start"
        headline={heading}
        headlineTag="h1"
      />

      {questions.map((questionElement, index) => (
        <div key={questionElement.question} className="suitability-question">
          <p
            className="suitability-question__text"
            dangerouslySetInnerHTML={{
              __html: `<strong>${index + 1}.</strong>&nbsp;${questionElement.question}`,
            }}
          />
          {questionElement.multiple_choice ? (
            <CheckboxList>
              {questionElement.answers.map((answerElement, answerIndex) => (
                <CheckboxField
                  key={`${questionElement.question}-${answerElement.answer}`}
                  id={`question-${index}-answer-${answerIndex}`}
                  name={`question-${index}`}
                  label={answerElement.answer}
                  checked={Boolean(
                    selectedAnswersByQuestion[questionElement.question]?.some(
                      (selected) => selected.answer === answerElement.answer
                    )
                  )}
                  onChange={(event) =>
                    handleMultipleChoiceAnswerChange(
                      questionElement.question,
                      answerElement.answer,
                      answerElement.is_suitable,
                      event.currentTarget.checked
                    )
                  }
                />
              ))}
            </CheckboxList>
          ) : (
            <div>
              {questionElement.answers.map((answer) => (
                <Button
                  key={`${questionElement.question}-${answer.answer}`}
                  variant={
                    selectedAnswersByQuestion[questionElement.question]?.[0]?.answer ===
                    answer.answer
                      ? 'primary'
                      : 'secondary'
                  }
                  aria-pressed={
                    selectedAnswersByQuestion[questionElement.question]?.[0]?.answer ===
                    answer.answer
                  }
                  onClick={() =>
                    handleSingleChoiceAnswerClick(
                      questionElement.question,
                      answer.answer,
                      answer.is_suitable
                    )
                  }
                >
                  {answer.answer}
                </Button>
              ))}
            </div>
          )}
        </div>
      ))}
      {showValidationError && (
        <Typography
          tag="p"
          variant="body"
          size="m"
          content={submit_error_message}
          className="error-message"
        />
      )}
      <Button onClick={handleSubmit} className="suitability-question__button-submit">
        {submit_button_label}
      </Button>
      <Modal isShowing={isModalOpen} closeModal={handleCloseModal}>
        {popup_image?.url && (
          <Image
            className="result-modal__image"
            src={popup_image.url}
            alt={popup_image.title ?? ''}
          />
        )}
        <Typography
          className="result-modal__message"
          tag="p"
          variant="body"
          size="m"
          content={submitResultMessage ?? ''}
        />
        <Button onClick={handleDownloadImage}>{download_button_label}</Button>
      </Modal>
    </Container>
  );
};
